import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Image,
  Video,
  Upload,
  Sparkles,
  Pencil,
  X,
  Loader2,
  Package,
  Target,
  Tags,
  Camera,
  FileSearch,
} from 'lucide-react'
import type { SourceType, Asset } from '@/types'
import { api } from '@/lib/api'
import { getAssetAspectRatioStyle, getAssetThumbnailUrl } from '@/lib/assets'
import { formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'

interface FormData {
  product_name: string
  description: string
  selling_points: string[]
  target_market: string
  target_audience: string
  price: string
  keywords: string[]
  competitor_brands: string[]
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    product_name: '',
    description: '',
    selling_points: [],
    target_market: '',
    target_audience: '',
    price: '',
    keywords: [],
    competitor_brands: [],
  })
  const [newKeyword, setNewKeyword] = useState('')
  const [newSellingPoint, setNewSellingPoint] = useState('')
  const [newCompetitor, setNewCompetitor] = useState('')
  const [assetTab, setAssetTab] = useState<SourceType | 'all'>('all')
  const screenshotInputRef = useRef<HTMLInputElement>(null)

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.getProduct(id!),
    enabled: !!id,
  })

  const { data: assets } = useQuery({
    queryKey: ['assets', id],
    queryFn: () => api.getAssets({ product_id: id, limit: 50, include_original_urls: false }),
    enabled: !!id,
  })

  // Initialize form data when product loads or when entering edit mode
  useEffect(() => {
    if (product && isEditing) {
      setFormData({
        product_name: product.product_name || '',
        description: product.description || '',
        // Parse selling_points from string to array (split by newline or semicolon)
        selling_points: product.selling_points
          ? product.selling_points.split(/[\n;]/).map((s) => s.trim()).filter(Boolean)
          : [],
        target_market: product.target_market || '',
        target_audience: product.target_audience || '',
        price: product.price || '',
        keywords: product.keywords || [],
        competitor_brands: product.competitor_brands || [],
      })
    }
  }, [product, isEditing])

  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      // Convert selling_points array to string for API
      const submitData = {
        ...data,
        selling_points: data.selling_points.join('\n'),
      }
      return api.updateProduct(id!, submitData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] })
      setIsEditing(false)
      toast({ title: 'Product updated successfully' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update product',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    },
  })

  const screenshotMutation = useMutation({
    mutationFn: (file: File) => api.uploadProductScreenshot(id!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['assets', id] })
      toast({ title: 'Screenshot updated' })
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to upload screenshot',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    },
  })

  const handleScreenshotSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      screenshotMutation.mutate(file)
    }
    // Reset so the same file can be re-selected
    e.target.value = ''
  }

  const handleSave = () => {
    if (!formData.product_name.trim()) {
      toast({ title: 'Product name is required', variant: 'destructive' })
      return
    }
    updateMutation.mutate(formData)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setNewKeyword('')
    setNewSellingPoint('')
    setNewCompetitor('')
  }

  const addToList = (
    field: 'keywords' | 'selling_points' | 'competitor_brands',
    value: string,
    setValue: (v: string) => void
  ) => {
    if (value.trim()) {
      setFormData({
        ...formData,
        [field]: [...formData[field], value.trim()],
      })
      setValue('')
    }
  }

  const removeFromList = (
    field: 'keywords' | 'selling_points' | 'competitor_brands',
    index: number
  ) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index),
    })
  }

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>
  }

  if (!product) {
    return <div className="text-center py-12">Product not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/products"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to products
          </Link>
          {isEditing ? (
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                className="pl-10 text-2xl font-bold"
                placeholder="Product name"
              />
            </div>
          ) : (
            <h1 className="text-2xl font-bold text-gray-900">{product.product_name}</h1>
          )}
          {!isEditing && product.description && (
            <p className="text-gray-600 mt-1">{product.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel} disabled={updateMutation.isPending}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Link to={`/generate/competitor-research?product=${id}`}>
                <Button>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Creative
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Product Screenshot */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Product Screenshot</h2>
        <input
          ref={screenshotInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleScreenshotSelect}
        />
        <div
          className="relative h-64 bg-gray-50 rounded-lg overflow-hidden cursor-pointer group"
          onClick={() => screenshotInputRef.current?.click()}
        >
          {product.screenshot_url ? (
            <>
              <img
                src={product.screenshot_url}
                alt="Product screenshot"
                className="w-full h-full object-contain"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white flex flex-col items-center gap-2">
                  {screenshotMutation.isPending ? (
                    <Loader2 className="h-8 w-8 animate-spin" />
                  ) : (
                    <>
                      <Camera className="h-8 w-8" />
                      <span className="text-sm font-medium">Change Screenshot</span>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 group-hover:text-gray-600 transition-colors">
              {screenshotMutation.isPending ? (
                <Loader2 className="h-10 w-10 animate-spin" />
              ) : (
                <>
                  <Upload className="h-10 w-10 mb-2" />
                  <span className="text-sm font-medium">Upload Screenshot</span>
                  <span className="text-xs mt-1">Click to select an image</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Product Info - Edit Mode */}
      {isEditing ? (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-semibold">Basic Information</h2>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <Input
                  placeholder="$0.00"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                placeholder="Describe your product..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            {/* Selling Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Selling Points</label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a selling point..."
                  value={newSellingPoint}
                  onChange={(e) => setNewSellingPoint(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addToList('selling_points', newSellingPoint, setNewSellingPoint)
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addToList('selling_points', newSellingPoint, setNewSellingPoint)}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.selling_points.map((point, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {point}
                    <button
                      type="button"
                      onClick={() => removeFromList('selling_points', idx)}
                      className="hover:text-blue-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-semibold">Target Market</h2>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Target className="w-4 h-4 inline mr-1" />
                  Target Market
                </label>
                <Input
                  placeholder="e.g., North America, Europe"
                  value={formData.target_market}
                  onChange={(e) => setFormData({ ...formData, target_market: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <Input
                  placeholder="e.g., Young professionals, 25-35"
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                />
              </div>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Tags className="w-4 h-4 inline mr-1" />
                Keywords
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a keyword..."
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addToList('keywords', newKeyword, setNewKeyword)
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addToList('keywords', newKeyword, setNewKeyword)}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-1"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeFromList('keywords', idx)}
                      className="hover:text-primary/80"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Competitors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Competitor Brands
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add a competitor brand..."
                  value={newCompetitor}
                  onChange={(e) => setNewCompetitor(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addToList('competitor_brands', newCompetitor, setNewCompetitor)
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addToList('competitor_brands', newCompetitor, setNewCompetitor)}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.competitor_brands.map((brand, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {brand}
                    <button
                      type="button"
                      onClick={() => removeFromList('competitor_brands', idx)}
                      className="hover:text-orange-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Product Info - View Mode */
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Product Details</h2>
          <dl className="grid grid-cols-2 gap-4">
            {product.target_market && (
              <div>
                <dt className="text-sm text-gray-500">Target Market</dt>
                <dd className="mt-1 text-gray-900">{product.target_market}</dd>
              </div>
            )}
            {product.target_audience && (
              <div>
                <dt className="text-sm text-gray-500">Target Audience</dt>
                <dd className="mt-1 text-gray-900">{product.target_audience}</dd>
              </div>
            )}
            {product.selling_points && (
              <div className="col-span-2">
                <dt className="text-sm text-gray-500">Selling Points</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  {product.selling_points
                    .split(/[\n;]/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((point, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-blue-50 text-blue-700 text-sm rounded"
                      >
                        {point}
                      </span>
                    ))}
                </dd>
              </div>
            )}
            {product.price && (
              <div>
                <dt className="text-sm text-gray-500">Price</dt>
                <dd className="mt-1 text-gray-900">{product.price}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-500">Created</dt>
              <dd className="mt-1 text-gray-900">{formatDate(product.created_at)}</dd>
            </div>
            {product.keywords.length > 0 && (
              <div className="col-span-2">
                <dt className="text-sm text-gray-500">Keywords</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  {product.keywords.map((keyword) => (
                    <span
                      key={keyword}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                    >
                      {keyword}
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {product.competitor_brands.length > 0 && (
              <div className="col-span-2">
                <dt className="text-sm text-gray-500">Competitor Brands</dt>
                <dd className="mt-1 flex flex-wrap gap-2">
                  {product.competitor_brands.map((brand) => (
                    <span
                      key={brand}
                      className="px-2 py-1 bg-orange-50 text-orange-700 text-sm rounded"
                    >
                      {brand}
                    </span>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Assets with Tabs */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Assets ({product.total_assets})</h2>
          </div>

          {/* Tab Buttons */}
          <div className="flex items-center gap-1 overflow-x-auto">
            <TabButton
              active={assetTab === 'all'}
              onClick={() => setAssetTab('all')}
              count={assets?.items.length || 0}
            >
              All
            </TabButton>
            <TabButton
              active={assetTab === 'screenshot'}
              onClick={() => setAssetTab('screenshot')}
              icon={Camera}
              count={assets?.items.filter((a) => a.source_type === 'screenshot').length || 0}
            >
              Screenshot
            </TabButton>
            <TabButton
              active={assetTab === 'uploaded'}
              onClick={() => setAssetTab('uploaded')}
              icon={Upload}
              count={product.uploaded_count}
            >
              Uploaded
            </TabButton>
            <TabButton
              active={assetTab === 'generated'}
              onClick={() => setAssetTab('generated')}
              icon={Sparkles}
              count={product.generated_count}
            >
              Generated
            </TabButton>
            <TabButton
              active={assetTab === 'reference'}
              onClick={() => setAssetTab('reference')}
              icon={FileSearch}
              count={assets?.items.filter((a) => a.source_type === 'reference').length || 0}
            >
              Reference
            </TabButton>
          </div>
        </div>

        {/* Assets Grid */}
        <AssetTabContent
          assets={assets?.items || []}
          filter={assetTab}
          productId={id!}
        />
      </div>
    </div>
  )
}

function TabButton({
  children,
  active,
  onClick,
  icon: Icon,
  count,
}: {
  children: React.ReactNode
  active: boolean
  onClick: () => void
  icon?: React.ComponentType<{ className?: string }>
  count: number
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap ${
        active
          ? 'bg-gray-900 text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
      <span className={`ml-1 ${active ? 'text-gray-300' : 'text-gray-400'}`}>
        ({count})
      </span>
    </button>
  )
}

function AssetTabContent({
  assets,
  filter,
  productId,
}: {
  assets: Asset[]
  filter: SourceType | 'all'
  productId: string
}) {
  const filteredAssets = filter === 'all'
    ? assets
    : assets.filter((a) => a.source_type === filter)

  if (filteredAssets.length === 0) {
    return (
      <div className="p-12 text-center">
        <Image className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 mb-4">
          {filter === 'all' ? 'No assets yet' : `No ${filter} assets`}
        </p>
        <div className="flex justify-center gap-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
          <Link to={`/generate/competitor-research?product=${productId}`}>
            <Button>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const SOURCE_TYPE_CONFIG: Record<SourceType, { label: string; color: string }> = {
    screenshot: { label: 'Screenshot', color: 'bg-blue-100 text-blue-700' },
    uploaded: { label: 'Uploaded', color: 'bg-green-100 text-green-700' },
    generated: { label: 'Generated', color: 'bg-purple-100 text-purple-700' },
    reference: { label: 'Reference', color: 'bg-orange-100 text-orange-700' },
    feedback_reference: { label: 'FB Ref', color: 'bg-gray-100 text-gray-700' },
    feedback_variant: { label: 'FB Variant', color: 'bg-gray-100 text-gray-700' },
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
      {filteredAssets.map((asset) => {
        const config = SOURCE_TYPE_CONFIG[asset.source_type]
        const thumbnailUrl = getAssetThumbnailUrl(asset)
        return (
          <div key={asset.id} className="relative group">
            <div
              className={`bg-gray-100 rounded-lg overflow-hidden ${getAssetAspectRatioStyle(asset) ? '' : 'aspect-square'}`}
              style={getAssetAspectRatioStyle(asset)}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={asset.description || 'Asset'}
                className="w-full h-full object-contain bg-gray-50"
                loading="lazy"
                decoding="async"
              />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50">
                  {asset.asset_type === 'video' ? (
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <Video className="h-8 w-8" />
                      <span className="text-[10px] font-medium uppercase tracking-wide">No thumbnail</span>
                    </div>
                  ) : (
                    <Image className="h-8 w-8 text-gray-300" />
                  )}
                </div>
              )}
            </div>
            {/* Source Type Badge */}
            <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
              {config.label}
            </div>
            {/* Score Badge */}
            {asset.score && (
              <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                {asset.score.toFixed(1)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
